import React, { useState, useEffect } from 'react';
import { collectionRequestAPI } from '../services/api';
import { 
  CollectionRequestListResponse, 
  CollectionRequest, 
  CreateCollectionRequest,
  CollectionStatus,
  UrgencyLevel,
  WasteType
} from '../types/collection';
import { CollectionStatusLabels, UrgencyLevelLabels, WasteTypeLabels } from '../types/collection';
import './CollectionRequestPage.css';

const CollectionRequestPage: React.FC = () => {
  const [requests, setRequests] = useState<CollectionRequestListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // 필터 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<CollectionStatus | ''>('');
  const [urgencyFilter, setUrgencyFilter] = useState<UrgencyLevel | ''>('');
  
  // 새 요청 폼 상태
  const [newRequest, setNewRequest] = useState<CreateCollectionRequest>({
    description: '',
    collection_address: '',
    waste_type: WasteType.FOOD,
    urgency: UrgencyLevel.MEDIUM,
    is_recurring: false
  });

  useEffect(() => {
    loadRequests();
  }, [currentPage, statusFilter, urgencyFilter]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        size: 10
      };
      
      if (statusFilter) params.status = statusFilter;
      if (urgencyFilter) params.urgency = urgencyFilter;
      
      const data = await collectionRequestAPI.getRequests(params);
      setRequests(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || '요청 목록을 불러오는 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await collectionRequestAPI.createRequest(newRequest);
      setShowCreateForm(false);
      setNewRequest({
        description: '',
        collection_address: '',
        waste_type: WasteType.FOOD,
        urgency: UrgencyLevel.MEDIUM,
        is_recurring: false
      });
      loadRequests();
    } catch (err: any) {
      setError(err.response?.data?.detail || '요청 생성 중 오류가 발생했습니다');
    }
  };

  const handleCancelRequest = async (requestId: number) => {
    if (!window.confirm('정말로 이 요청을 취소하시겠습니까?')) return;
    
    const reason = window.prompt('취소 사유를 입력하세요:');
    try {
      await collectionRequestAPI.cancelRequest(requestId, reason || undefined);
      loadRequests();
    } catch (err: any) {
      setError(err.response?.data?.detail || '요청 취소 중 오류가 발생했습니다');
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ko-KR');
  };

  if (loading && !requests) {
    return <div className="collection-request-page loading">로딩 중...</div>;
  }

  return (
    <div className="collection-request-page">
      <div className="collection-request-container">
        <div className="page-header">
          <h1>수거 요청 관리</h1>
          <button 
            onClick={() => setShowCreateForm(true)}
            className="create-btn"
          >
            새 수거 요청
          </button>
        </div>

        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => setError('')} className="close-error">×</button>
          </div>
        )}

        {/* 필터 섹션 */}
        <div className="filters">
          <div className="filter-group">
            <label>상태:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as CollectionStatus | '')}
            >
              <option value="">전체</option>
              {Object.values(CollectionStatus).map(status => (
                <option key={status} value={status}>{CollectionStatusLabels[status]}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>긴급도:</label>
            <select
              value={urgencyFilter}
              onChange={(e) => setUrgencyFilter(e.target.value as UrgencyLevel | '')}
            >
              <option value="">전체</option>
              {Object.values(UrgencyLevel).map(urgency => (
                <option key={urgency} value={urgency}>{UrgencyLevelLabels[urgency]}</option>
              ))}
            </select>
          </div>
        </div>

        {/* 요청 목록 */}
        <div className="requests-section">
          <div className="requests-header">
            <h2>수거 요청 목록</h2>
          </div>
          
          {requests?.items && requests.items.length > 0 ? (
            <>
              <div className="requests-grid">
                {requests.items.map((request) => (
                  <div key={request.id} className="request-card">
                    <div className="request-header">
                      <span className="request-id">요청 #{request.id}</span>
                      <div>
                        <span className={`status-badge ${request.status}`}>
                          {CollectionStatusLabels[request.status]}
                        </span>
                        <span className={`urgency-badge ${request.urgency}`}>
                          {UrgencyLevelLabels[request.urgency]}
                        </span>
                      </div>
                    </div>
                    
                    <div className="request-content">
                      <div><strong>주소:</strong> {request.collection_address}</div>
                      <div><strong>폐기물 유형:</strong> {WasteTypeLabels[request.waste_type]}</div>
                      {request.description && (
                        <div><strong>설명:</strong> {request.description}</div>
                      )}
                      <div><strong>수거 예정일:</strong> {request.collection_date ? formatDate(request.collection_date) : '미정'}</div>
                      <div><strong>생성일:</strong> {formatDate(request.created_at)}</div>
                    </div>
                    
                    {request.status === 'pending' && (
                      <div className="request-actions">
                        <button
                          onClick={() => handleCancelRequest(request.id)}
                          className="cancel-btn-small"
                        >
                          취소
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {/* 페이지네이션 */}
              {requests.pages > 1 && (
                <div className="pagination">
                  <button
                    className="page-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    이전
                  </button>
                  
                  {Array.from({ length: requests.pages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      className={`page-btn ${page === currentPage ? 'active' : ''}`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button
                    className="page-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === requests.pages}
                  >
                    다음
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="no-requests">
              {loading ? '로딩 중...' : '수거 요청이 없습니다.'}
            </div>
          )}
        </div>
      </div>

      {/* 새 요청 생성 폼 */}
      {showCreateForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>새 수거 요청</h2>
              <button onClick={() => setShowCreateForm(false)} className="close-btn">×</button>
            </div>
            <form onSubmit={handleCreateRequest} className="create-form">
              <div className="form-group">
                <label>폐기물 유형</label>
                <select
                  value={newRequest.waste_type}
                  onChange={(e) => setNewRequest(prev => ({ ...prev, waste_type: e.target.value as WasteType }))}
                  required
                >
                  {Object.values(WasteType).map(type => (
                    <option key={type} value={type}>{WasteTypeLabels[type]}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>상세 설명</label>
                <textarea
                  value={newRequest.description}
                  onChange={(e) => setNewRequest(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="폐기물에 대한 상세한 설명을 입력하세요 (최소 10자)"
                  required
                  minLength={10}
                  maxLength={1000}
                  rows={4}
                />
              </div>

              <div className="form-group">
                <label>수거 주소</label>
                <input
                  type="text"
                  value={newRequest.collection_address}
                  onChange={(e) => setNewRequest(prev => ({ ...prev, collection_address: e.target.value }))}
                  placeholder="수거할 주소를 입력하세요"
                  required
                  minLength={10}
                  maxLength={500}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>예상 용량 (L)</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={newRequest.estimated_volume || ''}
                    onChange={(e) => setNewRequest(prev => ({ 
                      ...prev, 
                      estimated_volume: e.target.value ? parseFloat(e.target.value) : undefined 
                    }))}
                    placeholder="예상 용량을 리터(L) 단위로 입력"
                  />
                </div>

                <div className="form-group">
                  <label>긴급도</label>
                  <select
                    value={newRequest.urgency}
                    onChange={(e) => setNewRequest(prev => ({ ...prev, urgency: e.target.value as UrgencyLevel }))}
                    required
                  >
                    {Object.values(UrgencyLevel).map(urgency => (
                      <option key={urgency} value={urgency}>{UrgencyLevelLabels[urgency]}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={newRequest.is_recurring}
                    onChange={(e) => setNewRequest(prev => ({ ...prev, is_recurring: e.target.checked }))}
                  />
                  정기 수거 요청
                </label>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setShowCreateForm(false)} className="cancel-btn">
                  취소
                </button>
                <button type="submit" className="submit-btn">
                  요청 생성
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollectionRequestPage;